'use client'
// components/three/mainScene.jsx
import { useEffect } from 'react'
import { useThree } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import useCamStore from '../../stores/camStore'
import useGameStore from '../../stores/gameStore'
import * as THREE from 'three/webgpu'
import { ServerNPCBridge } from './npc/ServerNPCBridge'
import { ThoughtBubble } from './npc/ThoughtBubble'
import { HalloweenHat } from './world/Halloween'
import { FirstPersonController } from './npc/FirstPersonController'
import { DetectionScanner } from './npc/DetectionScanner'
import { NPCInstancedAnimationProvider } from './npc/NPCInstancedAnimationProvider'
// The ambient crowd was removed 23 Sep (git history has AmbientNPCs.jsx).
import { DroneCameraDriver } from './DroneCamera'
import { PinoCam, pinoCamRequested } from './PinoCam'
import { VendingCam } from './world/VendingCam'
import { JobCentreCam } from './world/JobCentreCam'
import { PhotoCapture } from './PhotoCapture'
import { PaletteProvider } from '../../lib/world/paletteManager'
import { WorldEntities } from './world/WorldEntities'
import { FoodIndicators } from './world/FoodIndicators'
import { ResonanceField } from './world/ResonanceField'
import { Sacrifices } from './world/Sacrifices'
import { Visitors } from './world/Visitors'
import { BoughtThings } from './world/BoughtThings'
import { TownDressing } from './world/TownDressing'
import { SignalMarks } from './world/SignalMarks'
import { FEATURES } from '../../lib/features'
import { ObstacleProvider } from './obstacles/ObstacleProvider'
import { TerrainHeightProvider } from './terrain/TerrainHeightProvider'
import { ObstacleDebugRenderer } from './obstacles/ObstacleDebugRenderer'

// Collision wireframes: on when the URL has ?debugcollision (so it can never
// ship to prod by accident). The GLB is the only collision source: obstacles
// = orange, no-go zones = red.
const DEBUG_OBSTACLES =
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('debugcollision')

// The Pino cam (?cam=pino): the camera rides him instead of you.
const PINO_CAM = pinoCamRequested()

const _dir = new THREE.Vector3()
const _lookTarget = new THREE.Vector3()
function CamLogger() {
    const { camera } = useThree()
    const get = useThree((s) => s.get)

    // dev only: window.__survey([x, y, z], [lookX, lookY, lookZ]) puts the
    // free camera there, for looking at a part of town from somewhere the
    // walker cannot go (the monorail's route was checked from above with it)
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return
        window.__survey = (pos, look = [0, 0, 0]) => {
            useCamStore.setState({ orbitMode: true })
            const place = (tries) => requestAnimationFrame(() => {
                const controls = get().controls
                if (!controls && tries > 0) return place(tries - 1)
                camera.position.set(pos[0], pos[1], pos[2])
                if (controls?.target) { controls.target.set(look[0], look[1], look[2]); controls.update() }
                else camera.lookAt(look[0], look[1], look[2])
            })
            place(30)
        }
        return () => { delete window.__survey }
    }, [camera, get])

    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return
        const onKey = (e) => {
            if (e.key !== 'l' && e.key !== 'L') return
            camera.getWorldDirection(_dir)
            _lookTarget.copy(camera.position).add(_dir.multiplyScalar(10))
            const p = camera.position
            const l = _lookTarget
            const fmt = (v) => v.toFixed(2)
            const entry = `{ position: [${fmt(p.x)}, ${fmt(p.y)}, ${fmt(p.z)}], lookAt: [${fmt(l.x)}, ${fmt(l.y)}, ${fmt(l.z)}] },`
            console.log('[CamLogger]', entry)
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [camera])

    return null
}

export default function MainScene() {
    const orbitMode = useCamStore((s) => s.orbitMode)
    const droneActive = useGameStore((s) => s.droneActive)
    const vendingOpen = useGameStore((s) => s.vendingOpen)
    const jobsOpen = useGameStore((s) => s.jobsOpen)
    // Walking is the only way to be here. The camera is yours unless drone
    // (F) or dev free-look (O) borrows it.
    const fpEnabled = !orbitMode && !droneActive && !PINO_CAM && !vendingOpen && !jobsOpen
    return (
        <PaletteProvider>
            <ObstacleProvider modelPath="/models/obstacles.glb">
                {/* Terrain height field → bird ground-snap (client-side, visual). */}
                <TerrainHeightProvider modelPath="/models/terrain.glb">
                    {/* Bird NPCs - instanced skeletal animation + server bridge */}
                    <NPCInstancedAnimationProvider modelPath="/models/bird.glb" maxInstances={16}>
                        <ServerNPCBridge />
                        {/* the bubble over his head when there is something
                            in his bag, which a tap opens (npc/ThoughtBubble.jsx).
                            After the bridge, so it follows where he is drawn
                            this frame and not the frame before. */}
                        <ThoughtBubble />
                        {/* his witch's hat for the Halloween weekend, on his head bone (world/Halloween.jsx) */}
                        <HalloweenHat />
                    </NPCInstancedAnimationProvider>

                    {/* First-person walker — reuses the obstacle AABBs +
                        terrain heightfield in scope here. */}
                    <FirstPersonController enabled={fpEnabled} />
                    {/* FP recognition boxes: projects known objects, writes
                        to the detection mailbox for the DOM overlay. */}
                    <DetectionScanner />

                    {/* Community-submitted offerings, inside the provider so
                        each crystal can sit on the ground it landed on. It
                        used to mount below the closing tag, where
                        useTerrainHeight() returns null: the sim scatters
                        offerings at y=0 across a world with hills, so an
                        offering that landed on high ground was buried under
                        it and could not be found on foot. */}
                    <Sacrifices />
                    {/* Visitors (the octopus and whoever comes after it) sit
                        on the same ground the bird does, so they mount
                        inside the provider for the same reason. */}
                    <Visitors />
                    {/* What people bought him: lying where it landed until
                        he finds it, then at home. Inside the provider so it
                        sits on the ground it landed on. */}
                    <BoughtThings />
                </TerrainHeightProvider>

                {/* The town itself — one shared list with the director's set
                    (TownDressing), so nothing added here ever drifts out of
                    the other. Live-entity pieces stay below. */}
                <TownDressing />

                {/* World entity debug markers - shows where the agent thinks entities are */}
                <WorldEntities />

                {/* Visualises the spatial resonance field (artifacts + watch points) */}
                <ResonanceField />

                {/* Physical food items that appear/disappear as supply changes */}
                <FoodIndicators />

                {/* Walker signals — whistles + marks floating where sent. */}
                {FEATURES.signals && <SignalMarks />}

                <CamLogger />
                {/* Dev free-look — press O to orbit/pan/zoom freely. The
                    walker defers while this is mounted. */}
                {orbitMode && (
                    <OrbitControls makeDefault enableDamping target={[0, 3, 0]} />
                )}
                <DroneCameraDriver />
                {/* The close view of his vending machine: the camera at the glass while it is open. */}
                {vendingOpen && <VendingCam />}
                {/* And the job centre's, across the street from its window. */}
                {jobsOpen && !vendingOpen && <JobCentreCam />}
                {PINO_CAM && !orbitMode && !droneActive && <PinoCam />}
                {/* Services shutter presses from PhotoMode. */}
                <PhotoCapture />

                {/* Debug wireframes for the GLB collision set (?debugcollision). */}
                {DEBUG_OBSTACLES && <ObstacleDebugRenderer enabled={true} />}
            </ObstacleProvider>
        </PaletteProvider>
    )
}
